import apiClient from './client'
import { mockLogin } from './mockAuth'

interface LoginResponse {
  status: string
  username: string
  roles: string[]
  message: string
  token?: string
}

// Check if we're in development mode and should use mock auth
const USE_MOCK_AUTH = import.meta.env.DEV && import.meta.env.VITE_USE_MOCK_AUTH === 'true';

/**
 * Logowanie użytkownika
 * @param username Nazwa użytkownika
 * @param password Hasło
 * @returns Dane zalogowanego użytkownika
 */
export const loginUser = async (
  username: string,
  password: string
): Promise<LoginResponse> => {
  try {
    // Use mock auth in development if enabled
    if (USE_MOCK_AUTH) {
      return await mockLogin(username, password);
    }

    // Otherwise use real API
    console.log('🌐 auth.ts: Calling /login API for', username)
    const response = await apiClient.post('/login', {
      username,
      password
    });

    console.log('📡 auth.ts: Backend response:', response.data)
    const d: any = response.data;

    // Accept both formats:
    // 1) { success: true, user: { username, role }, message, token }
    // 2) { status: 'success', username, roles, message }
    if (d && typeof d === 'object') {
      if (d.success === true) {
        console.log('✅ auth.ts: Backend returned success=true format')
        const uname = d.user?.username ?? d.username ?? '';
        const roles = Array.isArray(d.roles)
          ? d.roles
          : (d.user?.role ? [d.user.role] : []);
        const msg = d.message ?? 'OK';
        const tok = d.token ?? '';
        const normalized = {
          status: 'success',
          username: uname,
          roles,
          message: msg,
          token: tok
        } as LoginResponse;
        console.log('📦 auth.ts: Normalized response:', normalized)
        return normalized;
      }
      if (typeof d.status === 'string') {
        console.log('✅ auth.ts: Backend returned status string format')
        return d as LoginResponse;
      }
    }
    // Fallback: normalize unknown shape
    console.log('⚠️ auth.ts: Unknown response format, returning error')
    return {
      status: 'error',
      username: '',
      roles: [],
      message: 'Nieprawidłowa odpowiedź logowania'
    } as LoginResponse;
  } catch (error: any) {
    console.error('❌ auth.ts: API error:', error)
    if (error.response && error.response.data) {
      console.log('📡 auth.ts: Error response data:', error.response.data)
      return error.response.data;
    }

    if (error.status === 'error') {
      // Handle mock auth error format
      return error as LoginResponse;
    }

    return {
      status: 'error',
      message: 'Błąd połączenia z serwerem',
      username: '',
      roles: []
    };
  }
}

/**
 * Logowanie przez Google OAuth
 * @param credential Google ID token
 * @returns Dane zalogowanego użytkownika
 */
export const loginWithGoogle = async (credential: string): Promise<LoginResponse> => {
  try {
    console.log('🌐 auth.ts: Calling /login/google API')
    const response = await apiClient.post('/login/google', {
      credential
    });

    console.log('📡 auth.ts: Google login response:', response.data)
    const d: any = response.data;

    // Backend returns: { success: true, token, user: { username, email, role, credits }, message }
    if (d && d.success === true) {
      console.log('✅ auth.ts: Google login successful')
      const normalized = {
        status: 'success',
        username: d.user?.username ?? '',
        roles: d.user?.role ? [d.user.role] : [],
        message: d.message ?? 'Logged in with Google',
        token: d.token ?? ''
      } as LoginResponse;
      console.log('📦 auth.ts: Normalized Google response:', normalized)
      return normalized;
    }

    // Error response
    console.log('⚠️ auth.ts: Google login failed:', d.message)
    return {
      status: 'error',
      username: '',
      roles: [],
      message: d.message || 'Google login failed'
    } as LoginResponse;

  } catch (error: any) {
    console.error('❌ auth.ts: Google login error:', error)
    if (error.response?.data) {
      const errorData = error.response.data;
      return {
        status: 'error',
        username: '',
        roles: [],
        message: errorData.message || 'Google authentication failed'
      };
    }

    return {
      status: 'error',
      message: 'Network error during Google login',
      username: '',
      roles: []
    };
  }
}

/**
 * Pobierz token JWT z localStorage
 * @returns Token JWT lub null
 */
export const getAuthToken = (): string | null => {
  return localStorage.getItem('token');
}
